/**
 * API Error Handling
 * Custom error classes and error handling utilities
 */

import { ApiClientResponse, ApiError } from "./api.types";

/**
 * Custom API Exception class
 */
export class ApiException extends Error {
  public status: number;
  public data?: any;
  public errors?: Array<{ field?: string; message: string }>;

  constructor(message: string, status = 500, data?: any) {
    super(message);
    this.name = "ApiException";
    this.status = status;
    this.data = data;

    // Maintains proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiException);
    }
  }

  toJSON(): ApiError {
    return {
      status: this.status,
      message: this.message,
      name: this.name,
      data: this.data,
      errors: this.errors,
    };
  }
}

/**
 * Network error class
 */
export class NetworkError extends ApiException {
  constructor(message = "Network error occurred") {
    super(message, 0);
    this.name = "NetworkError";
  }
}

/**
 * Not found error class
 */
export class NotFoundError extends ApiException {
  constructor(message = "Resource not found") {
    super(message, 404);
    this.name = "NotFoundError";
  }
}

/**
 * Validation error class
 */
export class ValidationError extends ApiException {
  constructor(
    message = "Validation failed",
    errors?: Array<{ field?: string; message: string }>
  ) {
    super(message, 400);
    this.name = "ValidationError";
    this.errors = errors;
  }
}

/**
 * Rate limit error class
 */
export class RateLimitError extends ApiException {
  public retryAfter?: number;

  constructor(message = "Rate limit exceeded", retryAfter?: number) {
    super(message, 429);
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
}

/**
 * Server error class
 */
export class ServerError extends ApiException {
  constructor(message = "Internal server error") {
    super(message, 500);
    this.name = "ServerError";
  }
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.name === "NetworkError" ||
      error.message.includes("fetch failed") ||
      error.message.includes("Failed to fetch") ||
      error.message.includes("Network request failed") ||
      error.message.includes("ERR_NETWORK") ||
      error.message.includes("ERR_INTERNET_DISCONNECTED")
    );
  }
  return false;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (error instanceof ApiException) {
    return error.status === 400 || error instanceof ValidationError;
  }
  return false;
}

/**
 * Parse error response and create appropriate error instance
 */
export function parseApiError(error: any): ApiException {
  // Already an ApiException
  if (error instanceof ApiException) {
    return error;
  }

  // Network/fetch errors
  if (isNetworkError(error)) {
    return new NetworkError(error.message || "Network error occurred");
  }

  // API error response
  if (error?.status) {
    const message = error.message || error.error || "An error occurred";

    switch (error.status) {
      case 400:
        return new ValidationError(message, error.errors);
      case 401:
      case 403:
        return new ApiException(message, error.status, error.data);
      case 404:
        return new NotFoundError(message);
      case 429:
        return new RateLimitError(message, error.retryAfter);
      case 500:
      case 502:
      case 503:
      case 504:
        return new ServerError(message);
      default:
        return new ApiException(message, error.status, error.data);
    }
  }

  // Generic error
  return new ApiException(
    error?.message || error?.toString() || "Unknown error occurred",
    500
  );
}

/**
 * Handle API errors and return standardized response
 */
export function handleApiError<T = any>(error: unknown): ApiClientResponse<T> {
  const apiError = parseApiError(error);

  // Log error in development
  if (process.env.NODE_ENV === "development") {
    console.error("[API Error]", {
      name: apiError.name,
      status: apiError.status,
      message: apiError.message,
      data: apiError.data,
      stack: apiError.stack,
    });
  }

  return {
    success: false,
    error: apiError.toJSON(),
  };
}

/**
 * Error message formatter
 */
export function formatErrorMessage(error: ApiError | undefined): string {
  if (!error) return "An unexpected error occurred";

  // If there are field-specific errors, format them
  if (error.errors && error.errors.length > 0) {
    return error.errors
      .map((e) => (e.field ? `${e.field}: ${e.message}` : e.message))
      .join(", ");
  }

  // Return main error message
  return error.message || "An error occurred";
}

/**
 * Retry helper for transient errors
 */
export function shouldRetry(error: unknown): boolean {
  if (error instanceof ApiException) {
    // Retry on network errors and server errors (5xx)
    return (
      error.status === 0 || // Network error
      error.status === 408 || // Timeout
      error.status === 429 || // Rate limit (with delay)
      error.status >= 500 // Server errors
    );
  }
  return isNetworkError(error);
}

/**
 * Get retry delay based on error type
 */
export function getRetryDelay(error: unknown, attemptNumber: number): number {
  if (error instanceof RateLimitError && error.retryAfter) {
    return error.retryAfter * 1000; // Convert to milliseconds
  }

  // Exponential backoff: 1s, 2s, 4s, 8s...
  return Math.min(1000 * Math.pow(2, attemptNumber - 1), 30000);
}
